import { legacyParseIsoFrenchDate } from "@/shared/utils/LegacyDates";
import { AppUserInfo, UserEmail, UserInfo } from "../AppUserInfo";
import { GenericPreferences } from "../GenericPreferences";
import { DomainPreferences } from "../DomainPreferences";
import { SitePreferences } from "../SitePreferences";
import { Domain, Site } from "../UserDomains";
import {
  UserDomainPermissions,
  UserFlags,
  UserPermissions,
} from "../UserPermissions";
import { UserPreferences } from "../UserPreferences";
import { ModuleSubscription } from "@/shared/model/modules/ModuleSubscription";
import { MyCawanModule } from "@/shared/model/modules/MyCawanModule";
import { isEmpty } from "@/shared/utils/Strings.ts";
import type {
  JsCacheCurrentUserOut,
  JsCacheDomainOut,
  JsCacheOut,
  JsCacheSiteOut,
} from "@/shared/endpoints/inout/JsCacheOut.ts";
import type { CurrentUserOut } from "@/shared/endpoints/inout/CurrentUserOut.ts";
import sendBroadcastAction from "@/shared/services/broadcast/SendBroadcastAction.ts";
import { compressToUTF16, decompressFromUTF16 } from "lz-string";

// Key for localStorage data coming from back end:
export const BACKEND_CACHE_KEY = "backend_cache";

// -----------------------------------------------------------------------------
// Structure of backend cache
// Do not export these interfaces, they are only used to clarify legacy
// "JsCache" structure, and used here to create an AppUserInfo instance
// -----------------------------------------------------------------------------

interface JsBackendCache {
  currentUser?: JsBackendCacheUser;
  domains: JsCacheDomainOut[];
  sites: {
    sites: JsBackendCacheSite[];
    afficheCode: boolean;
    triParCode: boolean;
  };
  sks: Record<string, string[]>; // keys are domain ids as string
  saas: Record<
    string,
    { siteId: number; dateDebut: string; dateFin: string | null }[]
  >;
}

type JsBackendCacheUser = JsCacheCurrentUserOut & {
  mailVerified: boolean;
  lang: string;
};

type JsBackendCacheSite = Omit<JsCacheSiteOut, "domainOrgName" | "label">;

export function clearBackendCache(): void {
  localStorage.removeItem(BACKEND_CACHE_KEY);
}

function readBackendCacheFromLocalStorage(): JsBackendCache | undefined {
  const storedItem = localStorage.getItem(BACKEND_CACHE_KEY);
  if (storedItem == null) return undefined;

  // Decompress the UTF-16 string
  const decompressed = decompressFromUTF16(storedItem);
  if (decompressed == "") return undefined; // corrupted or empty data

  try {
    return JSON.parse(decompressed) as JsBackendCache;
  } catch (e) {
    // SyntaxError: Unexpected token '@', "@@@" is not valid JSON
    console.log(e);
  }
  return undefined;
}

export function buildAppUserInfoFromLocalStorage(): AppUserInfo {
  const backendCache = readBackendCacheFromLocalStorage();
  if (backendCache == undefined) {
    return new AppUserInfo(false);
  }
  return buildAppUserInfoFromObject(backendCache);
}

export function updateLocalStorageAndBuildAppUserInfo(
  jsCache: JsCacheOut,
  user: CurrentUserOut,
): AppUserInfo {
  const cache = buildBackendCacheFromEndpointsResponses(jsCache, user);
  localStorage.setItem(
    BACKEND_CACHE_KEY,
    compressToUTF16(JSON.stringify(cache)),
  );
  sendBroadcastAction({ action: "backendDataRefreshed", payload: cache });
  return buildAppUserInfoFromJsBackendCache(cache);
}

function buildBackendCacheFromEndpointsResponses(
  jsCache: JsCacheOut,
  user: CurrentUserOut,
): JsBackendCache {
  const currentUser =
    jsCache.currentUser === undefined ?
      undefined
    : {
        ...jsCache.currentUser,
        mailVerified: user.mailVerified,
        lang: user.lang,
      };
  return {
    ...jsCache,
    currentUser,
  };
}

function buildAppUserInfoFromJsBackendCache(
  backEndCache: JsBackendCache,
): AppUserInfo {
  if (backEndCache.currentUser?.id == null) {
    return new AppUserInfo(false);
  }
  return new AppUserInfo(
    true,
    buildUserInfo(backEndCache),
    buildCurrentUserPermissions(backEndCache),
    buildUserPreferences(backEndCache),
  );
}

export function buildAppUserInfoFromObject(obj: object): AppUserInfo {
  return buildAppUserInfoFromJsBackendCache(obj as JsBackendCache);
}

function buildUserInfo(backEndCache: JsBackendCache): UserInfo | undefined {
  const user = backEndCache.currentUser;
  if (user?.id == null) {
    return undefined;
  }
  return new UserInfo(
    user.id.toString(),
    new UserEmail(user.mail, user.mailVerified),
    user.firstName,
    user.lastName,
    user.lang,
  );
}

function buildCurrentUserPermissions(
  backendCache: JsBackendCache,
): UserPermissions {
  return new UserPermissions(
    buildPermissionsByDomainId(backendCache),
    buildUserFlags(backendCache),
  );
}

function buildPermissionsByDomainId(
  backendCache: JsBackendCache,
): Map<string, UserDomainPermissions> {
  const res = new Map<string, UserDomainPermissions>();

  const subscriptions = buildSiteIdToSubscriptionsMap(backendCache);
  const sites = buildDomainIdToSitesMap(backendCache, subscriptions);

  for (const jsDomain of backendCache.domains) {
    const domainId = jsDomain.id.toString();
    res.set(
      domainId,
      new UserDomainPermissions(
        new Domain(
          domainId,
          jsDomain.organizationName,
          jsDomain.name,
          new DomainPreferences(recordToPreferences(jsDomain.preferences)),
          sites.get(domainId) ?? [],
        ),
        jsDomain.isUserAdmin,
        (backendCache.sks[domainId] ?? []).map(shortenPermKey),
      ),
    );
  }
  return res;
}

function shortenPermKey(permKey: string): string {
  return permKey.substring(permKey.lastIndexOf(".") + 1);
}

function buildDomainIdToSitesMap(
  backendCache: JsBackendCache,
  subscriptions: Map<string, ModuleSubscription[]>,
): Map<string, Site[]> {
  // Build map:
  const res = new Map<string, Site[]>();
  for (const site of backendCache.sites.sites) {
    const siteId = site.id.toString();
    const domainId = site.idDomain.toString();
    if (!res.has(domainId)) {
      res.set(domainId, []);
    }
    res
      .get(domainId)
      ?.push(
        new Site(
          domainId,
          siteId,
          site.nom,
          site.code,
          site.isPrefere,
          subscriptions.get(siteId) ?? [],
          new SitePreferences(recordToPreferences(site.preferences)),
        ),
      );
  }
  // Sort sites according to user preference:
  res.forEach((sites) => {
    sortSites(sites, backendCache.sites.triParCode);
  });
  return res;
}

function sortSites(sites: Site[], sortByCode: boolean): void {
  sites.sort((s1, s2) => {
    if (sortByCode) {
      const s1HasCode = !isEmpty(s1.code);
      const s2HasCode = !isEmpty(s2.code);

      if (s1HasCode && !s2HasCode) return -1;
      if (!s1HasCode && s2HasCode) return 1;
      if (s1HasCode && s2HasCode) {
        return s1.code.localeCompare(s2.code);
      }
      return s1.name.localeCompare(s2.name);
    }
    return s1.name.localeCompare(s2.name);
  });
}

function buildSiteIdToSubscriptionsMap(
  backendCache: JsBackendCache,
): Map<string, ModuleSubscription[]> {
  const res = new Map<string, ModuleSubscription[]>();
  Object.entries(backendCache.saas).forEach(([moduleId, sites]) => {
    for (const siteSubscription of sites) {
      const siteIdStr = siteSubscription.siteId.toString();
      if (!res.has(siteIdStr)) {
        res.set(siteIdStr, []);
      }
      res.get(siteIdStr)?.push(
        new ModuleSubscription(
          mapModule(moduleId),
          // eslint-disable-next-line @typescript-eslint/no-deprecated
          legacyParseIsoFrenchDate(siteSubscription.dateDebut),
          siteSubscription.dateFin != null ?
            // eslint-disable-next-line @typescript-eslint/no-deprecated
            legacyParseIsoFrenchDate(siteSubscription.dateFin)
          : undefined,
        ),
      );
    }
  });
  return res;
}

function buildUserFlags(backendCache: JsBackendCache): UserFlags {
  const user = backendCache.currentUser as {
    betaTester: boolean;
    commercial: boolean;
    developer: boolean;
    support: boolean;
  };
  if (user.developer) {
    return UserFlags.createDevelopper();
  } else if (user.support) {
    return UserFlags.createSupport();
  } else if (user.commercial) {
    return UserFlags.createSalesPerson();
  } else if (user.betaTester) {
    return UserFlags.createBetaTester();
  } else {
    return UserFlags.createNoFlags();
  }
}

function buildUserPreferences(backendCache: JsBackendCache): UserPreferences {
  return new UserPreferences(
    backendCache.sites.afficheCode,
    backendCache.sites.triParCode,
  );
}

function recordToPreferences(
  record: Record<string, string>,
): GenericPreferences {
  const map = new Map<string, string>();
  Object.entries(record).forEach(([key, value]) => {
    if (map.has(key)) {
      console.error(`Found duplicate key ${key} in record:`, record);
      throw new Error(`Duplicate key in record: ${key}`);
    }
    map.set(key, value);
  });
  return new GenericPreferences(map);
}

export function mapModule(key: string): MyCawanModule {
  const res = modulesMapping.get(key);
  if (res === undefined) {
    throw Error(`Missing MyCawanModule for key : ${key}`);
  }
  return res;
}

const modulesMapping = new Map<string, MyCawanModule>([
  ["gestion", MyCawanModule.ACHATS_GESTION],
  ["fullsoonapi", MyCawanModule.API_FULLSOON],
  ["linkeatapi", MyCawanModule.API_LINKEAT],
  ["francefrais", MyCawanModule.EDI_FRANCE_FRAIS],
  ["prevogel", MyCawanModule.EDI_PREVOGEL],
  ["stef", MyCawanModule.EDI_STEF],
  ["com.netresto.compta.saas.core.compta", MyCawanModule.COMPTABILITE],
  ["fds", MyCawanModule.REPARTITION_DE_SERVICE],
  ["caissescashpad", MyCawanModule.CAISSES_CASHPAD],
  ["caissescsi", MyCawanModule.CAISSES_CSI],
  ["caissesetc", MyCawanModule.CAISSES_ETC],
  ["caissesinnovorder", MyCawanModule.CAISSES_INNOVORDER],
  ["caissesladdition", MyCawanModule.CAISSES_LADDITION],
  ["caissesLightSpeed", MyCawanModule.CAISSES_LIGHTSPEED],
  ["caissesmedialog", MyCawanModule.CAISSES_MEDIALOG],
  ["caissespi", MyCawanModule.CAISSES_PI],
  ["caissespiaphilia", MyCawanModule.CAISSES_PIAPHILIA],
  ["caissespointex", MyCawanModule.CAISSES_POINTEX],
  ["caissestiller", MyCawanModule.CAISSES_TILLER],
  ["caissesZelty", MyCawanModule.CAISSES_ZELTY],
  ["caisseszeshop", MyCawanModule.CAISSES_ZESHOP],
  ["mycawanstaff", MyCawanModule.MYCAWANSTAFF],
  ["pointeuse", MyCawanModule.POINTEUSE],
  ["haccp", MyCawanModule.HACCP],
  [
    "sondesdisruptivetechnologies",
    MyCawanModule.SONDES_DISRUPTIVE_TECHNOLOGIES,
  ],
  ["com.netresto.rh.saas.core.rh", MyCawanModule.RH_GESTION_TEMPS],
  ["electronicsignatureyousign", MyCawanModule.SIGNATURE_ELECTRONIQUE],
  [
    "electronicsignatureachievedscheduleyousign",
    MyCawanModule.SIGNATURE_ELECTRONIQUE_HEURES_REALISEES,
  ],
  ["cegedim", MyCawanModule.EXPORT_PAIE_CEGEDIM],
  ["pleiades", MyCawanModule.EXPORT_PAIE_PLEIADES],
  ["com.netresto.export.sage.exportsage", MyCawanModule.EXPORT_PAIE_SAGE],
  ["silae", MyCawanModule.EXPORT_PAIE_SILAE],
  ["sage", MyCawanModule.SYNCHRO_SAGE],
  ["cegid", MyCawanModule.CEGID],
  ["ctrlsite", MyCawanModule.AUDITS],
  ["com.netresto.intranet.intranet", MyCawanModule.DOCUMENTS],
  ["energie", MyCawanModule.ENERGIE],
  ["fileexchange", MyCawanModule.IMPORT_EXPORT],
]);
